import React, { useEffect, useState } from 'react';
import { CommonCard } from '@/components/common/CommonCard';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { Settings } from 'lucide-react';
import { CommonSelect } from '@/components/common/CommonSelect';
import { IRole } from '@/interfaces/Role/role.interface';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface IGlobalSettings {
    id?: string;
    tax: string;
    supportNumber: string;
    fixDeliveryCharge: string;
    fixCourierCharge: string;
    deliveryFeeAfterRadiusPerKm: string;
    platformCharge: string;
    minimumOrderValue: string;
    deliveryRadius: string; // Keeping this as it was previously migrated and might be used as default
    returnWindowHours: string;
    driverBaseFare: string;
    driverPerKmRate: string;
    maxActiveOrdersPerDriver: string;
    // driverMinimumGuarantee: string;
    // driverSurgeBonus: string;
    storeDefaultRoleId?: string;
    taxStatus: 'active' | 'inactive';
}

interface IGlobalSettingsResponse {
    id?: string;
    tax?: string;
    supportNumber?: string;
    fixDeliveryCharge?: string;
    fixCourierCharge?: string;
    deliveryFeeAfterRadiusPerKm?: string;
    platformCharge?: string;
    minimumOrderValue?: string;
    deliveryRadius?: string;
    returnWindowHours?: string;
    driverBaseFare?: string;
    driverPerKmRate?: string;
    maxActiveOrdersPerDriver?: string;
    // driverMinimumGuarantee?: string;
    // driverSurgeBonus?: string;
    storeDefaultRoleId?: string;
    taxStatus?: 'active' | 'inactive';
}

const AdminSettings = () => {
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    const [settings, setSettings] = useState<IGlobalSettings>({
        tax: '0',
        supportNumber: '',
        fixDeliveryCharge: '0',
        fixCourierCharge: '0',
        deliveryRadius: '5',
        deliveryFeeAfterRadiusPerKm: '0',
        platformCharge: '0',
        minimumOrderValue: '0',
        returnWindowHours: '0',
        driverBaseFare: '0',
        driverPerKmRate: '0',
        maxActiveOrdersPerDriver: '1',
        // driverMinimumGuarantee: '0',
        // driverSurgeBonus: '0',
        storeDefaultRoleId: '',
        taxStatus: 'inactive'
    });

    const [roles, setRoles] = useState<IRole[]>([]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setFetching(true);
                const response = await callApi<{ data: IGlobalSettingsResponse | IGlobalSettingsResponse[] }>(
                    `${API_URL}${API_ENDPOINT.settings}`,
                    { method: 'GET' }
                );

                const settingsData = Array.isArray(response?.data) ? response?.data[0] : response?.data;

                if (settingsData) {
                    setSettings({
                        id: settingsData.id,
                        tax: settingsData.tax?.toString() || '',
                        supportNumber: settingsData.supportNumber || '',
                        fixDeliveryCharge: settingsData.fixDeliveryCharge?.toString() || '',
                        fixCourierCharge: settingsData.fixCourierCharge?.toString() || '',
                        deliveryRadius: settingsData.deliveryRadius?.toString() || '',
                        deliveryFeeAfterRadiusPerKm: settingsData.deliveryFeeAfterRadiusPerKm?.toString() || '',
                        platformCharge: settingsData.platformCharge?.toString() || '',
                        minimumOrderValue: settingsData.minimumOrderValue?.toString() || '',
                        returnWindowHours: settingsData.returnWindowHours?.toString() || '',
                        driverBaseFare: settingsData.driverBaseFare?.toString() || '',
                        driverPerKmRate: settingsData.driverPerKmRate?.toString() || '',
                        maxActiveOrdersPerDriver: settingsData.maxActiveOrdersPerDriver?.toString() || '1',
                        // driverMinimumGuarantee: settingsData.driverMinimumGuarantee?.toString() || '0',
                        // driverSurgeBonus: settingsData.driverSurgeBonus?.toString() || '0',
                        storeDefaultRoleId: settingsData.storeDefaultRoleId || '',
                        taxStatus: settingsData.taxStatus || 'inactive'
                    });
                }
            } catch (error) {
                console.error("Failed to fetch settings:", error);
            } finally {
                setFetching(false);
            }
        };

        const fetchRoles = async () => {
            try {
                const res = await callApi<any>(
                    `${API_URL}${API_ENDPOINT.roles}`,
                    { method: 'GET' }
                );
                const rolesList = res?.data?.roles || res?.data || res || [];
                setRoles(Array.isArray(rolesList) ? rolesList : []);
            } catch (error) {
                console.error("Failed to fetch roles:", error);
            }
        };

        fetchSettings();
        fetchRoles();
    }, []);

    const handleSave = async () => {
        setLoading(true);
        try {
            const url = settings.id
                ? `${API_URL}${API_ENDPOINT.settings}?settingId=${settings.id}`
                : `${API_URL}${API_ENDPOINT.settings}`;

            await callApi(
                url,
                {
                    method: 'POST',
                    data: {
                        tax: Number(settings.tax),
                        supportNumber: settings.supportNumber,
                        fixDeliveryCharge: Number(settings.fixDeliveryCharge),
                        fixCourierCharge: Number(settings.fixCourierCharge),
                        deliveryFeeAfterRadiusPerKm: Number(settings.deliveryFeeAfterRadiusPerKm),
                        platformCharge: Number(settings.platformCharge),
                        minimumOrderValue: Number(settings.minimumOrderValue),
                        returnWindowHours: Number(settings.returnWindowHours),
                        driverBaseFare: Number(settings.driverBaseFare),
                        driverPerKmRate: Number(settings.driverPerKmRate),
                        maxActiveOrdersPerDriver: Number(settings.maxActiveOrdersPerDriver || 1),
                        // driverMinimumGuarantee: Number(settings.driverMinimumGuarantee),
                        // driverSurgeBonus: Number(settings.driverSurgeBonus),
                        storeDefaultRoleId: settings.storeDefaultRoleId,
                        taxStatus: settings.taxStatus
                    }
                }
            );
            showToastNotification('success', 'Settings updated successfully');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof IGlobalSettings, value: any) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    if (fetching) {
        return (
            <div className="flex justify-center items-center py-12 bg-card border rounded-xl">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <CommonCard
                title="Global Settings"
                icon={<Settings className="text-primary w-5 h-5" />}
                headerAction={
                    <Button
                        onClick={handleSave}
                        isLoading={loading}
                        className="px-6 h-10 bg-[#901B1B] hover:bg-[#7a1616] text-white rounded-md text-sm transition-colors shadow-sm"
                    >
                        Update Settings
                    </Button>
                }
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 p-2">
                    <FormField
                        label="VAT Tax (%)"
                        name="tax"
                        type="number"
                        value={settings.tax}
                        disabled={settings.taxStatus === 'inactive'}
                        onChange={(e) => handleChange('tax', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <div className="flex flex-col space-y-2 justify-center">
                        <span className="text-sm font-semibold text-foreground">Tax Status</span>
                        <div className="flex items-center space-x-3 h-10">
                            <Switch
                                id="taxStatus"
                                checked={settings.taxStatus === 'active'}
                                onCheckedChange={(checked) => handleChange('taxStatus', checked == true ? "active" : "inactive")}
                            />
                            <Label htmlFor="taxStatus" className="text-sm font-medium cursor-pointer text-muted-foreground">
                                Apply Tax on Orders: <span className={settings.taxStatus === 'active' ? 'text-green-600 font-semibold' : 'text-slate-500 font-semibold'}>{settings.taxStatus === 'active' ? 'Applicable' : 'Not Applicable'}</span>
                            </Label>
                        </div>
                    </div>

                    <FormField
                        label="Support Number"
                        name="supportNumber"
                        type="text"
                        value={settings.supportNumber}
                        onChange={(e) => handleChange('supportNumber', e.target.value)}
                        placeholder="e.g. 3387464849"
                        autoComplete="off"
                        inputClass="w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Store Delivery Charge"
                        name="fixDeliveryCharge"
                        type="number"
                        value={settings.fixDeliveryCharge}
                        onChange={(e) => handleChange('fixDeliveryCharge', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Store Courier Charge"
                        name="fixCourierCharge"
                        type="number"
                        value={settings.fixCourierCharge}
                        onChange={(e) => handleChange('fixCourierCharge', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Delivery Fee After Radius (Per Km)"
                        name="deliveryFeeAfterRadiusPerKm"
                        type="number"
                        value={settings.deliveryFeeAfterRadiusPerKm}
                        onChange={(e) => handleChange('deliveryFeeAfterRadiusPerKm', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Platform Charge"
                        name="platformCharge"
                        type="number"
                        value={settings.platformCharge}
                        onChange={(e) => handleChange('platformCharge', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Minimum Order Value"
                        name="minimumOrderValue"
                        type="number"
                        value={settings.minimumOrderValue}
                        onChange={(e) => handleChange('minimumOrderValue', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="0.00"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Return Window (Hours)"
                        name="returnWindowHours"
                        type="number"
                        value={settings.returnWindowHours}
                        onChange={(e) => handleChange('returnWindowHours', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="e.g. 24 or 48"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Driver Base Fare"
                        name="driverBaseFare"
                        type="number"
                        value={settings.driverBaseFare}
                        onChange={(e) => handleChange('driverBaseFare', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="e.g. 2"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Driver Per Km Rate"
                        name="driverPerKmRate"
                        type="number"
                        value={settings.driverPerKmRate}
                        onChange={(e) => handleChange('driverPerKmRate', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="e.g. 2"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Max Active Orders Per Driver"
                        name="maxActiveOrdersPerDriver"
                        type="number"
                        value={settings.maxActiveOrdersPerDriver}
                        onChange={(e) => handleChange('maxActiveOrdersPerDriver', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="1"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    {/* <FormField
                        label="Driver Minimum Guarantee"
                        name="driverMinimumGuarantee"
                        type="number"
                        value={settings.driverMinimumGuarantee}
                        onChange={(e) => handleChange('driverMinimumGuarantee', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="e.g. 2"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    />

                    <FormField
                        label="Driver Surge Bonus"
                        name="driverSurgeBonus"
                        type="number"
                        value={settings.driverSurgeBonus}
                        onChange={(e) => handleChange('driverSurgeBonus', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="e.g. 2"
                        autoComplete="off"
                        inputClass="no-spinner w-full h-10 px-3 py-2 rounded-md border bg-background text-sm text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    /> */}

                    <div className="flex flex-col">
                        <FormField
                            label="Store Default Role"
                            name="storeDefaultRoleId"
                            type="select"
                            value={settings.storeDefaultRoleId || ''}
                            onValueChange={(val) => handleChange('storeDefaultRoleId', val)}
                            options={roles.map(r => ({ label: r.name, value: r.id }))}
                        />
                    </div>
                </div>

                {/* <div className="flex justify-end mt-8 border-t pt-6">
                    <Button
                        onClick={handleSave}
                        isLoading={loading}
                        className="px-10 h-11 bg-[#901B1B] hover:bg-[#7a1616] text-white rounded-md transition-colors"
                    >
                        Update Settings
                    </Button>
                </div> */}
            </CommonCard>
        </div>
    );
};

export default AdminSettings;
